import React, { useState, useEffect, useContext } from 'react';
import { callApi } from '../callApi';
import { AppContext } from '../components/AppContext';
import {
  Box, Grid, Flex, Text, Button, Link,
  Table, Thead, Tbody, Tr, Th, Td, VStack, Heading,
  Spinner, useToast, Select, Input, HStack
} from "@chakra-ui/react";
import HeaderMenus from './HeaderMenus';

function ModernCaseTable() {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();
  const [filters, setFilters] = useState({
    search_term: '',
    limit: '50',
  });

  const { accessToken } = useContext(AppContext);

  const bgColor = 'white';
  const borderColor = 'gray.200';
  const tableHeaderBg = 'gray.50';

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      loadCaseData();
    }
  };

  const handleClearFilters = () => {
    const defaultFilters = {
      search_term: '',
      limit: '50',
    };
    setFilters(defaultFilters);
    loadCaseData(defaultFilters);
  };

  const loadCaseData = async (currentFilters = filters) => {
    setIsLoading(true);
    setError(null);
    
    const params = new URLSearchParams();
    if (currentFilters.search_term) params.append('search_term', currentFilters.search_term);
    if (currentFilters.limit) params.append('limit', currentFilters.limit);
    const queryString = params.toString();

    try {
      const response = await callApi(`/getcases_modern?${queryString}`, accessToken);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load case data');
      }
      
      const result = await response.json();
      
      if (result.status === 'success') {
        setData(result);
        
        toast({
          title: "Data loaded",
          description: "Case data refreshed successfully",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      } else {
        throw new Error('Failed to load case data');
      }
    } catch (err) {
      const errorMessage = `Error loading case data: ${err.message}`;
      setError(errorMessage);
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCaseData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'None') return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Box>
      <HeaderMenus />
      
      <Box bg={bgColor} p={6} minH="calc(100vh - 60px)">
        <Box mb={6} p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
          <Heading size="sm" mb={4}>Filters</Heading>
          <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4} alignItems="end">
            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">Search</Text>
              <Input 
                name="search_term" 
                value={filters.search_term} 
                onChange={handleFilterChange}
                onKeyDown={handleKeyDown}
                placeholder="Case ID, Patient Name..."
              />
            </Box>
            
            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">Results</Text>
              <Select name="limit" value={filters.limit} onChange={handleFilterChange}>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </Select>
            </Box>
            <HStack spacing={4}>
              <Button colorScheme="blue" onClick={() => loadCaseData()} isLoading={isLoading}>Apply Filters</Button>
              <Button variant="outline" onClick={handleClearFilters}>Clear</Button>
            </HStack>
          </Grid>
        </Box>

        {error && (
          <Box p={4} mb={4} borderRadius="md" borderWidth="1px" borderColor="red.300" bg="red.50">
            <Text color="red.600">{error}</Text>
          </Box>
        )}
        
        {isLoading && (
          <Flex justify="center" align="center" height="200px">
            <Spinner size="xl" color="blue.500" />
          </Flex>
        )}

        {!isLoading && data && (
          <Box>
            <Flex justifyContent="space-between" alignItems="center" mb={4}>
              <Heading size="md">IONM Case Results ({data.total_count} found)</Heading>
              <Button 
                colorScheme="blue" 
                size="sm" 
                onClick={() => loadCaseData()}
                isLoading={isLoading}
              >
                Refresh Data
              </Button>
            </Flex>
            
            {data.cases.length > 0 ? (
              <Box overflowX="auto" borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                <Table variant="simple" size="sm">
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      <Th>Patient ID</Th>
                      <Th>Patient</Th>
                      <Th>Case Name</Th>
                      <Th>Procedure Date</Th>
                      <Th>Last Updated</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.cases.map((caseItem) => (
                      <Tr key={caseItem.case_id}>
                        <Td
                          onClick={() => {
                            const url = `/ionmform?caseid=${caseItem.case_id}`;
                            window.open(url, "_blank");
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <Text fontWeight="medium" color="blue.600">
                            {caseItem.external_patient_id}
                          </Text>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text>{caseItem.patient_first_name} {caseItem.patient_last_name}</Text>
                            <Text fontSize="xs" color="gray.500">{caseItem.external_patient_id}</Text>
                          </VStack>
                        </Td>
                        <Td title={caseItem.case_name}>
                          {caseItem.case_name?.substring(0, 50) + (caseItem.case_name?.length > 50 ? '...' : '')}
                        </Td>
                        <Td>{formatDate(caseItem.procedure_date)}</Td>
                        <Td>{formatDate(caseItem.updated_at)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            ) : (
              <Box p={4} textAlign="center" borderRadius="md" borderWidth="1px">
                <Text>No cases found matching your criteria.</Text>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default ModernCaseTable;